/**
 * Componente RioMap - Mapa interativo com funcionalidades avançadas
 *
 * Funcionalidades principais:
 * - Renderização de mapa interativo com marcadores, polígonos e círculos
 * - Suporte a clusters de marcadores
 * - Menu de contexto personalizado
 * - Desenho de áreas de interesse e waypoints
 * - Controles de zoom e centralização
 * - Suporte a diferentes tipos de marcadores (início, fim, caminhão, etc)
 * - Formatação de coordenadas geográficas
 * - Eventos de clique e arraste de marcadores
 * - Modal para edição de áreas de interesse
 * - Integração com Redux para gerenciamento de estado
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import without from "lodash/fp/without";
import Circle, { CirclePosition } from "@rio-cloud/rio-uikit/Circle";
import ClusterMapMarker from "@rio-cloud/rio-uikit/ClusterMapMarker";
import ContextMenu from "@rio-cloud/rio-uikit/ContextMenu";
import ContextMenuItem from "@rio-cloud/rio-uikit/ContextMenuItem";
import EventUtils, { createEnhancedMapEvent } from "@rio-cloud/rio-uikit/EventUtils";
import MapAdditionalLayerSettings from "@rio-cloud/rio-uikit/MapAdditionalLayerSettings";
import Map from "@rio-cloud/rio-uikit/Map";
import MapSettings from "@rio-cloud/rio-uikit/MapSettings";
import { MapApi, MapEvent, Position } from "@rio-cloud/rio-uikit/mapTypes";
import MapTypeSettings from "@rio-cloud/rio-uikit/MapTypeSettings";
import Marker, { MarkerProps } from "@rio-cloud/rio-uikit/Marker";
import MarkerLayer from "@rio-cloud/rio-uikit/MarkerLayer";
import Polygon from "@rio-cloud/rio-uikit/Polygon";
import SingleMapMarker from "@rio-cloud/rio-uikit/SingleMapMarker";
import { useAppSelector } from "@store";

import { useMapDrawingContext } from "../../providers/MapDrawingContext";
import { defineAvoidAreas, defineWaypoint } from "../../store/routes/RoutesSlice";
import { calculateOffsetCoordinates } from "../Dynamics/DynamicContentArea";
import ModalMapInterestArea from "../ModalMapInterestArea";
import { useClusterEventMap } from "./hooks/useClusterEventMap";
import { useVehicleAutonomy } from "./hooks/useVehicleAutonomy";
import MapWorkshopPoiSettingsItem from "@rio-cloud/rio-uikit/MapWorkshopPoiSettingsItem";

import {
  ICirclePosition,
  IContextMenuData,
  ICoordinates,
  IGeofanceData,
  IMarkerPosition,
  IModalData,
  IPointPosition,
  IRioMapProps,
  ISandboxMarker,
} from "./interfaces";

import { AutonomySegments, CurrencySegments, Segments } from "./components";
import {
  convertMarkerPositionsToNumber,
  formatCoordinates,
  getFormattedCoordinate,
  getIconByType,
  getMarkerColorByType,
  getStyleByType,
} from "./functions";

import { fetchAddressWithLatLong } from "src/services/routes";
import * as St from "./styles";
import { formatPolygon, mountPolygonPoints } from "@utils";
import { InterestPoint } from "src/types/interestPoints";

export * from "./components";
export * from "./functions";
export { getBoundingBox, getRandomValueToForceRerender } from "./functions";
export * from "./hooks";
export * from "./interfaces";
export * from "./utils";

const RioMap = ({
  apiKey,
  position,
  boundingBox,
  segments,
  markers,
  currencySegments,
  useClusters,
  onSelectVehicle,
  zoom,
  showInterestPoints = true,
  showAreas = true,
  onMapClick,
}: IRioMapProps) => {
  const dispatch = useDispatch();
  const mapApiRef = useRef<MapApi>();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const { areasRework } = useAppSelector((state) => state.routes);
  const { areasToAvoid } = useAppSelector((state) => state.routes);
  const [contextMenuData, setContextMenuData] = useState<IContextMenuData>({});
  const [openModal, setOpenModal] = useState(false);
  const [coordLabel, setCoordLabel] = useState<string>("");
  const [showSpinner, setShowSpinner] = useState(true);
  const [modalData, setModalData] = useState<IModalData>({} as IModalData);
  const [modalDatas, setModalDatas] = useState<IModalData[]>([]);
  const drawingMode = useRef<"interest" | "avoid" | "">("");
  const closeDrawingMode = useRef<"interest" | "avoid" | "">("");
  const originalCoord = useRef<ICoordinates | null>(null);
  const interestAreas = useRef<ICoordinates[]>([]);
  const avoidAreas = useRef<ICoordinates[]>([]);
  const [markersPosition, setMarkersPosition] = useState<IMarkerPosition[]>([]);
  const [polygonPositions, setPolygonPositions] = useState<IPointPosition[]>([]);
  const [circlePositions, setCirclePositions] = useState<CirclePosition[]>([]);
  const [allMarkers, setAllMarkers] = useState<ISandboxMarker[]>([]);
  const [showCustomerPois, setShowCustomerPois] = useState(true);
  const [activePoiLayers, setActivePoiLayers] = useState<string[]>([]);
  const [showInterestPointsMap, setShowInterestPointsMap] = useState(showInterestPoints);

  const {
    handleMapClick,
    setDrawingPoints,
    sortPointsClockwise,
    circleDrawing,
    polygonDrawing,
    drawingMarkers,
    enableDrawingRef,
    enableContextMenuRef,
    mapZoom,
    setMapZoom,
    centerMarker,
    mapCenter,
    setMapCenter,
    interestPoints,
  } = useMapDrawingContext();

  const autonomyState = useAppSelector((state) => state.autonomy);
  const { enableAutonomy, expanded } = autonomyState;
  const { vehicleRangeSegments, autonomyData, autonomyLimitPosition, hasAutonomyForFullRoute } = useVehicleAutonomy({
    segments,
  });

  useEffect(() => {
    if (position) {
      setMapCenter({ lat: position.lat ?? 0, lng: position.lng ?? 0 });
    }
  }, [position]);

  useEffect(() => {
    if (zoom) {
      setMapZoom(zoom);
    }
  }, [zoom]);

  useEffect(() => {
    setAllMarkers([...markers, ...drawingMarkers]);
  }, [markers, drawingMarkers]);

  const fixedMarkers = markers.filter(
    (marker: any) =>
      marker.markerProps?.iconNames?.includes("start") ||
      marker.markerProps?.iconNames?.includes("finish") ||
      marker.markerProps?.iconNames?.includes("truck")
  );

  const clusterMarkers = markers.filter(
    (marker: any) =>
      !marker.markerProps?.iconNames?.includes("start") &&
      !marker.markerProps?.iconNames?.includes("finish") &&
      !marker.markerProps?.iconNames?.includes("truck")
  );

  const simpleTheme = useMemo(
    () => ({
      getNoisePresentation: (marker: ISandboxMarker) => {
        return (
          <SingleMapMarker
            name={marker.markerProps?.name ?? ""}
            markerColor={marker.markerProps?.markerColor}
            iconNames={marker.markerProps?.iconNames}
            anchorSize={marker.markerProps?.anchorSize}
            markerOnHover={marker.markerProps?.markerOnHover}
            active={marker.markerProps?.active}
            fixed={marker.markerProps?.fixed}
          />
        );
      },
      getClusterPresentation: (markers: ISandboxMarker[]) => (
        <ClusterMapMarker count={markers.length} warningCount={0} exceptionCount={0} />
      ),
    }),
    []
  );

  const clusterEventMap = useClusterEventMap({
    onSelectVehicle,
    setMapCenter,
    setMapZoom,
    mapApiRef,
    mapContainerRef,
  });

  const handleOpenContextMenu = (contextMenuCoordinates: Position, waypointId?: string) => {
    setShowSpinner(true);
    setCoordLabel(formatCoordinates(contextMenuCoordinates));
    originalCoord.current = contextMenuCoordinates;

    setContextMenuData((prev) => ({
      ...prev,
      target: waypointId || "",
    }));

    setTimeout(() => setShowSpinner(false), 500);
  };

  const mapEventListenerMap = useMemo(
    () => ({
      [EventUtils.CONTEXTMENU]: (event: any) => {
        if (!enableContextMenuRef.current) {
          return;
        }

        if (mapApiRef.current) {
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();

          const coords = mapApiRef.current.map.screenToGeo(event.viewportX, event.viewportY);

          if (coords) {
            const contextData = { target: "map", event };
            setContextMenuData(contextData);

            handleOpenContextMenu({ lat: coords.lat, lng: coords.lng });
          }
        }
      },
      [EventUtils.TAP]: async (event: any) => {
        if (EventUtils.isRightClick(event)) {
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }

        const map = mapApiRef.current?.map;
        if (!map) return;

        const enhancedEvent = createEnhancedMapEvent(event, map);
        if ("calculateGeoCoords" in enhancedEvent) {
          const coords = enhancedEvent.calculateGeoCoords();
          if (coords && enableDrawingRef.current && handleMapClick) {
            handleMapClick({ lat: coords.lat, lng: coords.lng });
          }

          if (onMapClick && coords) {
            const response = await fetchAddressWithLatLong(coords.lat, coords.lng);
            const address = response.pop()?.label || "Novo Ponto de Interesse";
            onMapClick(address, { lat: coords.lat, lng: coords.lng });
          }
        }
      },
    }),
    [handleMapClick]
  );

  const handleMenuClick = (option: string) => {
    const currentCoord = originalCoord.current;

    if (!currentCoord) {
      return;
    }

    switch (option) {
      case "add-waypoint": {
        if (!currentCoord) return;

        handleAddWaypoint({
          lat: currentCoord.lat,
          lng: currentCoord.lng,
        });

        setTimeout(() => {
          setContextMenuData({});
        }, 100);
        return;
      }
      case "copy-coordinates": {
        if (!currentCoord) return;

        handleCopyCoordinates({
          lat: currentCoord.lat,
          lng: currentCoord.lng,
        });

        setTimeout(() => {
          setContextMenuData({});
        }, 100);
        return;
      }
    }
  };

  const handleAddWaypoint = (position: ICoordinates) => {
    if (!position) {
      return;
    }

    const newWaypoint = {
      id: uuidv4(),
      ...position,
    };

    dispatch(defineWaypoint(newWaypoint));
  };

  const handleCopyCoordinates = async (position: ICoordinates) => {
    const response = await fetchAddressWithLatLong(position.lat, position.lng);
    if (!position) return;

    try {
      const formattedCoords = formatCoordinates(position, undefined, "simple");
      await navigator.clipboard.writeText(formattedCoords);
    } catch (error) {
      const formattedCoords = formatCoordinates(position, undefined, "simple");
      const textArea = document.createElement("textarea");
      textArea.value = formattedCoords;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
      } catch (fallbackError) {
        console.error("❌ [RioMap] Erro no fallback de cópia:", fallbackError);
      }

      document.body.removeChild(textArea);
    }
  };

  function createGeofenceInMap(geofenceData: IGeofanceData) {
    if (geofenceData.type === "circle") {
      setCirclePositions((prevState: any) => [
        ...prevState,
        {
          position: geofenceData.coordinates[0],
          radius: geofenceData.radius,
          type: geofenceData.geofenceCategory === "INTEREST_AREA" ? "interest" : "avoid",
          eventListenerMap: mapEventListenerMap,
        },
      ]);
    } else {
      setPolygonPositions((prevState) => [
        ...prevState,
        {
          points: geofenceData.coordinates,
          type: geofenceData.geofenceCategory === "INTEREST_AREA" ? "interest" : "avoid",
          eventListenerMap: mapEventListenerMap,
        },
      ]);
    }
  }

  const removedSome = (removedItems: IModalData[], prev: IModalData) => {
    return removedItems.some((r) => r.id === prev.id);
  };

  const handleSendData = (removedItems: IModalData[], newModalDatas: IModalData[]) => {
    setModalDatas((prev) => {
      const filtered = prev.filter((p) => !removedSome(removedItems, p));
      return [...filtered, ...newModalDatas];
    });
  };

  useEffect(() => {
    const updatedItems = [...areasRework];
    const currentIds = modalDatas.map((m) => m.id);
    const addedItems = updatedItems.filter((area) => !currentIds.includes(area.id?.toString() ?? ""));
    const removedItems = modalDatas.filter((m) => !updatedItems.some((a) => a.id?.toString() === m.id));

    removedItems.forEach((item) => {
      handleCancel(item);
    });

    const newModalDatas: IModalData[] = [];
    const newCircles: ICirclePosition[] = [];
    const newPolygons: IPointPosition[] = [];
    let avoidFormattedStrings: string[] = [];

    addedItems.forEach((area) => {
      const isCircle = area.circleRadius !== null && area.circleRadius !== undefined;
      const geofenceId = area.id?.toString() ?? uuidv4();
      const rawInterestType = (area.interestType ?? "").toUpperCase().trim();
      const type = rawInterestType === "INTEREST" ? "interest" : "avoid";

      let formatted: string;
      if (isCircle) {
        const center = area.points[0];
        const radius = area.circleRadius!;
        const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
        formatted = `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;
      } else {
        formatted = formatPolygon(area.points);
      }

      const geofenceData: IGeofanceData = {
        geofenceId,
        type: isCircle ? "circle" : "polygon",
        coordinates: area.points,
        radius: isCircle ? area.circleRadius ?? undefined : undefined,
        geofenceCategory: type === "interest" ? "INTEREST_AREA" : "AVOID_AREA",
      };

      createGeofenceInMap(geofenceData);

      newModalDatas.push({
        id: geofenceId,
        type,
        coordinates: area.points,
        radius: isCircle ? area.circleRadius ?? null : null,
        formatted,
      });

      if (type === "avoid") {
        avoidFormattedStrings.push(formatted);
      }

      if (isCircle) {
        newCircles.push({
          id: geofenceId,
          position: area.points[0],
          radius: area.circleRadius ?? 0,
          type,
          eventListenerMap: mapEventListenerMap,
        });
      } else {
        newPolygons.push({
          id: geofenceId,
          points: area.points,
          type,
          eventListenerMap: mapEventListenerMap,
        });
      }
    });

    handleSendData(removedItems, newModalDatas);
  }, [areasRework]);

  const isSamePosition = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    return a.lat === b.lat && a.lng === b.lng;
  };

  const polygonMatches = (
    polygon: { points: { lat: number; lng: number }[] },
    coords: { lat: number; lng: number }[]
  ) => {
    return polygon.points.some((point) => coords.some((coord) => isSamePosition(coord, point)));
  };

  const handleCancel = (data: IModalData) => {
    if (data.radius) {
      setCirclePositions((prevState) =>
        prevState.filter(
          (circle) => circle.position.lat !== data.coordinates[0].lat || circle.position.lng !== data.coordinates[0].lng
        )
      );
    } else {
      setPolygonPositions((prevState) => prevState.filter((polygon) => !polygonMatches(polygon, data.coordinates)));
    }

    clearMarkersPosition();

    if (data.type === "interest") {
      interestAreas.current = [];
    } else if (data.type === "avoid") {
      avoidAreas.current = [];

      let canceledAreaString = "";
      if (data.radius == null) {
        canceledAreaString = "polygon:" + data.coordinates.map((coord) => `${coord.lat},${coord.lng}`).join(";");
      } else {
        canceledAreaString =
          "corridor:" +
          data.coordinates.map((coord) => `${coord.lat},${coord.lng}`).join(";") +
          ";r=" +
          Math.round(data.radius);
      }

      const currentFormatted = areasToAvoid.formatted || "";

      const updatedArray = currentFormatted
        .split("|")
        .map((item) => item.trim())
        .filter((item) => item !== "" && item !== canceledAreaString.trim());

      const newFormatted = updatedArray.length > 0 ? updatedArray.join("|") : undefined;
      dispatch(defineAvoidAreas({ formatted: newFormatted }));
    }
  };

  const handleSave = () => {
    clearMarkersPosition();
    if (closeDrawingMode.current === "interest") {
      interestAreas.current = [];
    }
    if (closeDrawingMode.current === "avoid") {
      avoidAreas.current = [];
    }
    drawingMode.current = "";
    originalCoord.current = null;
    setModalData({} as IModalData);
  };

  function clearMarkersPosition() {
    setMarkersPosition((prevState) => prevState.filter((marker) => marker.type !== closeDrawingMode.current));
  }

  const contextMenuItems = [
    <ContextMenuItem
      key="add-waypoint"
      icon="rioglyph rioglyph-pushpin"
      label="Adicionar ponto de passagem"
      callback={() => handleMenuClick("add-waypoint")}
    />,
    <ContextMenuItem
      key="add-poi"
      icon="rioglyph rioglyph-poi"
      label="Adicionar ponto de interesse"
      callback={() => handleMenuClick("add-poi")}
    />,
    <ContextMenuItem
      key="copy-coordinates"
      icon="rioglyph rioglyph-copy"
      label="Copiar coordenadas"
      callback={() => handleMenuClick("copy-coordinates")}
    />,
  ];

  useEffect(() => {
    if (!circleDrawing) {
      setCirclePositions((prevState) => prevState.filter((circle) => !circle.new));
      return;
    }

    const { center, radius, type } = circleDrawing;
    if (!radius || !center) return;

    const newCircle: ICirclePosition = {
      id: uuidv4(),
      position: center,
      radius,
      type: type === "interest" ? "interest" : "avoid",
      eventListenerMap: mapEventListenerMap,
      new: true,
    };

    setCirclePositions((prevState: any) => {
      const updatedState = prevState.map((circle: any) => (circle.new ? { ...circle, ...newCircle } : circle));
      const exists = updatedState.some((circle: any) => circle.new);
      return exists ? updatedState : [...updatedState, newCircle];
    });
  }, [circleDrawing]);

  useEffect(() => {
    if (!polygonDrawing) {
      setPolygonPositions((prevState) => prevState.filter((polygon) => !polygon.new));
      return;
    }

    const newPolygon = {
      id: uuidv4(),
      points: polygonDrawing.points,
      type: polygonDrawing.type,
      eventListenerMap: mapEventListenerMap,
      new: true,
    };

    setPolygonPositions((prevState) => {
      const updatedState = prevState.map((polygon) => (polygon.new ? { ...polygon, ...newPolygon } : polygon));
      const exists = updatedState.some((polygon) => polygon.new);
      return exists ? updatedState : [...updatedState, newPolygon];
    });
  }, [polygonDrawing]);

  const createMarkerDragListeners = (markerId: string) => {
    let originCoords: ICoordinates | null = null;

    return {
      [EventUtils.DRAG_START]: (event: MapEvent) => {
        if (!mapApiRef.current || originCoords) return;
        mapApiRef.current?.behavior?.disable((window as any).H.mapevents.Behavior.DRAGGING);
        originCoords = mapApiRef.current.map.screenToGeo(
          event.currentPointer.viewportX,
          event.currentPointer.viewportY
        );
      },
      [EventUtils.DRAG]: (event: MapEvent) => {
        if (!mapApiRef.current) return;
        const marker = event.target;
        const coords = mapApiRef.current.map.screenToGeo(
          event.currentPointer.viewportX,
          event.currentPointer.viewportY
        );
        marker.setGeometry(coords);
      },
      [EventUtils.DRAG_END]: (event: MapEvent) => {
        if (!mapApiRef.current) return;
        mapApiRef.current?.behavior?.enable((window as any).H.mapevents.Behavior.DRAGGING);
        const marker = event.target;
        const coords = marker.getGeometry();

        if (handleMapClick && markerId.includes("circle-center")) {
          handleMapClick(coords);
        } else {
          const newPolygonPoints = polygonPositions.map((polygon) => {
            const updatedPoints = mountPolygonPoints(polygon.points, originCoords, coords);

            return {
              ...polygon,
              points: updatedPoints,
            };
          });

          if (newPolygonPoints.map((polygon) => polygon.points).flat().length >= 3) {
            const sortedPoints = sortPointsClockwise(newPolygonPoints.map((polygon) => polygon.points).flat());
            setDrawingPoints([...sortedPoints]);
          }
        }
        originCoords = null;
      },
    };
  };

  const addOrRemove = (layers: string[], value: string) =>
    layers.includes(value) ? without([value])(layers) : [...layers, value];

  const handleToggleCustomerPois = () => {
    setShowCustomerPois(!showCustomerPois);
    setActivePoiLayers(addOrRemove(activePoiLayers, "CUSTOMER_POIS"));
    setShowInterestPointsMap(!showInterestPointsMap);
  };

  const filterInterestPoints = (interestPoints: InterestPoint[], markers: MarkerProps[]) => {
    return interestPoints?.filter((point) => {
      if (markers.length > 0) {
        const editingMarker = markers[0];
        const isSamePosition =
          Math.abs(point.latitude - editingMarker.position.lat) < 0.0001 &&
          Math.abs(point.longitude - editingMarker.position.lng) < 0.0001;
        return !isSamePosition;
      }
      return true;
    });
  };

  useEffect(() => {
    if (!mapApiRef.current) return;
    const mapObjects = mapApiRef.current.map.getObjects();

    allMarkers.forEach((marker) => {
      if (marker.id.includes("circle-center") || marker.id.includes("polygon-point")) {
        const hMarker = mapObjects.find(
          (obj: any) => obj instanceof (window as any).H.map.Marker && obj.getData?.()?.id === marker.id
        );
        if (hMarker && !hMarker.draggable) {
          hMarker.draggable = true;
          hMarker.setData?.({ id: marker.id });
        }
      }
    });
  }, [allMarkers]);

  const layers = [
    <MapWorkshopPoiSettingsItem
      key="mapCustomerPoiSettingsItem"
      isActive={showCustomerPois}
      onToggle={handleToggleCustomerPois}
      label="Workshop POIs"
    />,
  ];

  console.log({ interestPoints, markers });

  return (
    <St.Container ref={mapContainerRef} className="height-100pct margin-0">
      <Map
        credentials={{ apikey: apiKey }}
        language="pt-BR"
        center={mapCenter}
        boundingBox={centerMarker ? undefined : boundingBox}
        zoom={mapZoom}
        hideMapSettings
        mapSettings={
          <MapSettings
            options={[
              <MapTypeSettings
                key="mapTypeSettings"
                tooltip="Change map type"
                dropdownHeaderText="Map views"
                defaultTypeLabel="Default view"
                truckTypeLabel="Truck view"
                terrainTypeLabel="Terrain view"
                satelliteTypeLabel="Satellite view"
                nightTypeLabel="Night view"
              />,
              <MapAdditionalLayerSettings
                key="mapLayerSettingsPoi"
                layers={layers}
                activeLayers={activePoiLayers}
                tooltip="Toggle various POI layers"
              />,
            ]}
          />
        }
        eventListenerMap={EventUtils.createTapOnDblTapPreventer({
          [EventUtils.TAP]: mapEventListenerMap[EventUtils.TAP],
          [EventUtils.CONTEXTMENU]: mapEventListenerMap[EventUtils.CONTEXTMENU],
        })}
      >
        {(api: MapApi) => {
          mapApiRef.current = api;
          return (
            <>
              {segments && <Segments segments={segments} enableAutonomy={enableAutonomy} expanded={expanded} />}
              {vehicleRangeSegments.length > 0 && (
                <>
                  <AutonomySegments
                    key={`autonomy-segment-${autonomyData.isEnabled}-${autonomyData.fuelLevel}-${autonomyData.consumption}`}
                    segments={vehicleRangeSegments}
                  />

                  {autonomyLimitPosition && !hasAutonomyForFullRoute && (
                    <Marker
                      key={`autonomy-limit-${autonomyLimitPosition.lat}-${autonomyLimitPosition.lng}`}
                      position={{ lat: autonomyLimitPosition.lat, lng: autonomyLimitPosition.lng }}
                      icon={
                        <SingleMapMarker
                          name={"Limite de Autonomia"}
                          markerColor="bg-map-marker-success"
                          iconNames={["rioglyph rioglyph-filling-station"]}
                          anchorSize="md"
                        />
                      }
                      draggable={false}
                      eventListenerMap={{}}
                    />
                  )}
                </>
              )}
              {currencySegments && <CurrencySegments segments={currencySegments} />}
              {fixedMarkers.map((marker: any, index: number) => (
                <Marker
                  key={`Marker1-${marker.id}-${index}-${new Date().getTime()}`}
                  customData={{ id: marker.id, data: marker.data }}
                  position={marker.position}
                  icon={<SingleMapMarker {...marker.markerProps} />}
                  draggable={true}
                  eventListenerMap={createMarkerDragListeners(marker.id)}
                />
              ))}
              {useClusters && clusterMarkers.length > 0 && (position || boundingBox) ? (
                <MarkerLayer
                  data={convertMarkerPositionsToNumber(clusterMarkers)}
                  simpleTheme={simpleTheme}
                  eventListenerMap={clusterEventMap}
                />
              ) : (
                <>
                  {allMarkers.map((marker, index) => (
                    <Marker
                      key={`Marker2-${marker.id}-${index}`}
                      position={marker.position}
                      icon={<SingleMapMarker {...marker.markerProps} />}
                      draggable={marker.id.includes("circle-center") || marker.id.includes("polygon-point")}
                      eventListenerMap={createMarkerDragListeners(marker.id)}
                    />
                  ))}
                </>
              )}
              <>
                {showInterestPointsMap &&
                  (showCustomerPois ? (
                    <MarkerLayer
                      data={convertMarkerPositionsToNumber(
                        filterInterestPoints(interestPoints, markers)?.map((res) => {
                          const isConcessionaria = res.categoria?.toLowerCase().includes("concession");
                          return {
                            id: res.id?.toString(),
                            markerProps: {
                              iconNames: isConcessionaria
                                ? ["rioglyph rioglyph-workshop"]
                                : ["rioglyph rioglyph-map-marker"],
                              name: res.nome,
                              markerColor: isConcessionaria ? "bg-map-marker-poi" : "bg-map-marker-info",
                            },
                            position: {
                              lat: res.latitude,
                              lng: res.longitude,
                            },
                          };
                        })
                      )}
                      simpleTheme={simpleTheme}
                      eventListenerMap={clusterEventMap}
                    />
                  ) : (
                    filterInterestPoints(interestPoints, markers)?.map((point, index) => (
                      <Marker
                        key={`interest-point-${point.id ?? index}`}
                        position={{ lat: point.latitude, lng: point.longitude }}
                        icon={
                          <SingleMapMarker
                            name={point.nome || point.endereco || "Ponto de Interesse"}
                            markerColor={
                              point.categoria === "Concessionária" ? "bg-map-marker-poi" : "bg-map-marker-info"
                            }
                            iconNames={
                              point.categoria === "Concessionária"
                                ? ["rioglyph rioglyph-workshop"]
                                : ["rioglyph rioglyph-map-marker"]
                            }
                            anchorSize="md"
                          />
                        }
                        draggable={false}
                        eventListenerMap={{}}
                      />
                    ))
                  ))}
              </>
              {markersPosition.map((markerPosition, index) => (
                <Marker
                  key={`markersPosition-${index}`}
                  position={markerPosition.position}
                  draggable={true}
                  icon={
                    <SingleMapMarker
                      name={
                        <div className="display-flex align-items-center gap-10 padding-3 margin-x-3">
                          <span className={`text-anchorSize-h4 ${getIconByType(markerPosition.type)}`} />
                          <div className="line-height-16">
                            <div className="text-medium">{markerPosition.title}</div>
                            <div className="text-anchorSize-12">
                              {getFormattedCoordinate(
                                markerPosition.position.lat,
                                markerPosition.position.lat > 0 ? "N" : "S"
                              )}{" "}
                              {getFormattedCoordinate(
                                markerPosition.position.lng,
                                markerPosition.position.lng > 0 ? "E" : "W"
                              )}
                            </div>
                          </div>
                        </div>
                      }
                      markerColor={getMarkerColorByType(markerPosition.type)}
                      markerOnHover
                      anchorSize="lg"
                    />
                  }
                  eventListenerMap={createMarkerDragListeners(markerPosition.title)}
                />
              ))}
              {showAreas ? (
                <React.Fragment>
                  {polygonPositions.map((pointPosition, index) => (
                    <Polygon
                      key={`Polygon-${index}-${new Date().getTime()}`}
                      points={pointPosition.points}
                      style={getStyleByType(pointPosition.type)}
                    />
                  ))}
                  {circlePositions.map((circlePosition, index) => (
                    <Circle
                      key={`Circle-${index}-${new Date().getTime()}`}
                      position={circlePosition.position}
                      radius={circlePosition.radius}
                      style={getStyleByType(circlePosition.type)}
                      precision={30}
                    />
                  ))}
                </React.Fragment>
              ) : null}
              {enableContextMenuRef.current && (
                <ContextMenu
                  key={`ContextMenu-${coordLabel}`}
                  onOpen={handleOpenContextMenu}
                  menuItems={contextMenuItems}
                  contextMenuEvent={contextMenuData.event}
                />
              )}
              {!enableContextMenuRef.current && (
                <div style={{ display: "none" }}>{/* Menu de contexto desabilitado */}</div>
              )}
            </>
          );
        }}
      </Map>
      <ModalMapInterestArea
        handleSave={handleSave}
        handleCancel={handleCancel}
        modalData={modalData}
        openModal={openModal}
        setOpenModal={setOpenModal}
      />
    </St.Container>
  );
};

export default RioMap;
